/** Paramètres → Import / Export : clients et produits, colonnes rapprochées avant import. */
import { useRef, useState } from "react";
import { facturxFetch } from "../../api/client";
import { Button } from "../../components/Button";
import { useCompany } from "../../company/CompanyProvider";
import { PriorTurnover } from "./PriorTurnover";

type Entity = "clients" | "products";

type Skip = { line: number; message: string };

type ImportResult = {
  created: number;
  updated: number;
  skipped_total?: number;
  skipped?: Skip[];
};

type FieldDef = { id: string; label: string; required?: boolean; aliases: string[] };

type CsvColumn = { key: string; label: string };

type Staged = {
  filename: string;
  csv: string;
  columns: CsvColumn[];
  sample: Record<string, string>;
  map: Record<string, string>;
};

const TITLES: Record<Entity, string> = {
  clients: "Clients",
  products: "Produits",
};

const CLIENT_FIELDS: FieldDef[] = [
  { id: "name", label: "Nom", required: true, aliases: ["name", "client", "raison_sociale", "nom", "client_name"] },
  { id: "display_name", label: "Nom d’affichage", aliases: ["display_name", "nom_affichage"] },
  { id: "siret", label: "SIRET", aliases: ["siret"] },
  { id: "siren", label: "SIREN", aliases: ["siren"] },
  { id: "vat_number", label: "N° TVA", aliases: ["vat_number", "tva", "vat"] },
  { id: "id_number", label: "N° d’identification", aliases: ["id_number", "number"] },
  { id: "tax_exempt", label: "Exonéré de TVA", aliases: ["tax_exempt"] },
  { id: "email", label: "E-mail", aliases: ["email"] },
  { id: "phone", label: "Téléphone", aliases: ["phone", "telephone"] },
  { id: "website", label: "Site web", aliases: ["website", "site"] },
  { id: "address_line", label: "Adresse", required: true, aliases: ["address_line", "address1", "adresse"] },
  { id: "address_line2", label: "Complément d’adresse", aliases: ["address_line2", "address2"] },
  { id: "postal_code", label: "Code postal", required: true, aliases: ["postal_code", "code_postal", "zip"] },
  { id: "city", label: "Ville", required: true, aliases: ["city", "ville"] },
  { id: "region", label: "Région", aliases: ["region", "state"] },
  { id: "country_code", label: "Pays", aliases: ["country_code", "country", "country_id"] },
  { id: "ship_address_line", label: "Livraison — adresse", aliases: ["ship_address_line", "shipping_address1"] },
  { id: "ship_address_line2", label: "Livraison — complément", aliases: ["ship_address_line2", "shipping_address2"] },
  { id: "ship_postal_code", label: "Livraison — code postal", aliases: ["ship_postal_code", "shipping_postal_code"] },
  { id: "ship_city", label: "Livraison — ville", aliases: ["ship_city", "shipping_city"] },
  { id: "ship_region", label: "Livraison — région", aliases: ["ship_region", "shipping_state"] },
  { id: "ship_country_code", label: "Livraison — pays", aliases: ["ship_country_code", "shipping_country_id", "shipping_country"] },
  { id: "contact_first_name", label: "Contact — prénom", aliases: ["contact_first_name", "first_name"] },
  { id: "contact_last_name", label: "Contact — nom", aliases: ["contact_last_name", "last_name"] },
  { id: "contact_email", label: "Contact — e-mail", aliases: ["contact_email"] },
  { id: "contact_phone", label: "Contact — téléphone", aliases: ["contact_phone"] },
];

const PRODUCT_FIELDS: FieldDef[] = [
  { id: "label", label: "Libellé", required: true, aliases: ["label", "product_key", "nom", "libelle", "name"] },
  { id: "product_code", label: "Code", aliases: ["product_code", "code"] },
  { id: "description", label: "Description", aliases: ["description", "notes"] },
  { id: "price_ht", label: "Prix HT", aliases: ["price_ht", "price", "unit_price", "prix"] },
  { id: "cost_ht", label: "Coût HT", aliases: ["cost_ht", "cost", "cout"] },
  { id: "quantity", label: "Quantité", aliases: ["quantity", "default_qty", "qte"] },
  { id: "tax_rate", label: "TVA", aliases: ["tax_rate", "tax_rate1", "tva"] },
  { id: "unit", label: "Unité", aliases: ["unit", "unite"] },
  { id: "op_kind", label: "Nature", aliases: ["op_kind", "type"] },
  { id: "active", label: "Actif", aliases: ["active"] },
];

function fieldsOf(entity: Entity): FieldDef[] {
  return entity === "clients" ? CLIENT_FIELDS : PRODUCT_FIELDS;
}

function normHeader(raw: string): string {
  return raw
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

function parseCsv(csv: string): { columns: CsvColumn[]; sample: Record<string, string> } {
  const text = csv.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const firstBreak = text.indexOf("\n");
  const firstLine = firstBreak === -1 ? text : text.slice(0, firstBreak);
  if (firstLine.trim() === "") return { columns: [], sample: {} };
  const delim = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ";" : ",";
  const records = csvRecords(text, delim);
  const heads = records[0] || [];
  const columns: CsvColumn[] = [];
  const seen = new Set<string>();
  heads.forEach((label) => {
    const key = normHeader(label);
    if (!key || seen.has(key)) return;
    seen.add(key);
    columns.push({ key, label: label.trim() || key });
  });
  const sample: Record<string, string> = {};
  const data = records[1];
  if (data) {
    heads.forEach((label, i) => {
      const key = normHeader(label);
      if (key && sample[key] === undefined) sample[key] = (data[i] || "").trim();
    });
  }
  return { columns, sample };
}

function csvRecords(text: string, delim: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i++;
        } else quoted = false;
      } else cur += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delim) {
      row.push(cur);
      cur = "";
    } else if (ch === "\n") {
      row.push(cur);
      if (row.some((cell) => cell.trim() !== "")) rows.push(row);
      row = [];
      cur = "";
    } else cur += ch;
  }
  if (cur !== "" || row.length > 0) {
    row.push(cur);
    if (row.some((cell) => cell.trim() !== "")) rows.push(row);
  }
  return rows;
}

function guessMap(entity: Entity, columns: CsvColumn[]): Record<string, string> {
  const keys = new Set(columns.map((c) => c.key));
  const map: Record<string, string> = {};
  for (const field of fieldsOf(entity)) {
    map[field.id] = field.aliases.find((a) => keys.has(a)) || "";
  }
  return map;
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function clip(value: string): string {
  const t = value.replace(/\s+/g, " ").trim();
  return t.length > 42 ? `${t.slice(0, 42)}…` : t;
}

function Block({ entity }: { entity: Entity }) {
  const { company } = useCompany();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [skipped, setSkipped] = useState<Skip[]>([]);
  const [staged, setStaged] = useState<Staged | null>(null);
  const fields = fieldsOf(entity);

  if (!company) return null;
  const companyId = company.id;
  const missing = staged ? fields.filter((f) => f.required && !staged.map[f.id]) : [];
  const used = new Set(staged ? Object.values(staged.map).filter(Boolean) : []);
  const unused = staged ? staged.columns.filter((c) => !used.has(c.key)) : [];

  async function exp() {
    setBusy(true);
    setErr("");
    setMsg("");
    setSkipped([]);
    try {
      const data = await facturxFetch<{ filename: string; csv: string; count: number }>(
        `import-export.php?company_id=${companyId}&entity=${entity}`,
      );
      downloadCsv(data.filename, data.csv);
      setMsg(`${data.count} ${entity === "clients" ? "client(s)" : "produit(s)"} exporté(s).`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Export impossible");
    } finally {
      setBusy(false);
    }
  }

  async function onFile(file: File) {
    setErr("");
    setMsg("");
    setSkipped([]);
    const csv = await file.text();
    const parsed = parseCsv(csv);
    if (parsed.columns.length === 0) {
      setStaged(null);
      setErr("Aucune colonne dans ce fichier.");
      return;
    }
    setStaged({
      filename: file.name,
      csv,
      columns: parsed.columns,
      sample: parsed.sample,
      map: guessMap(entity, parsed.columns),
    });
  }

  async function runImport() {
    if (!staged || missing.length > 0) return;
    setBusy(true);
    setErr("");
    setMsg("");
    setSkipped([]);
    try {
      const data = await facturxFetch<ImportResult>("import-export.php", {
        method: "POST",
        body: JSON.stringify({ company_id: companyId, entity, csv: staged.csv, map: staged.map }),
      });
      const left = data.skipped_total ?? data.skipped?.length ?? 0;
      setSkipped(data.skipped || []);
      setMsg(`${data.created} créé(s), ${data.updated} mis à jour${left ? `, ${left} ignoré(s)` : ""}.`);
      setStaged(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Import impossible");
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
    }
  }

  return (
    <section className={`fx-co-card${staged ? " fx-ie-card" : ""}`}>
      <header className="fx-co-card__head">
        <h2>{TITLES[entity]}</h2>
        <p>
          {entity === "clients"
            ? "Chaque champ FacturX est listé à gauche. À droite, la colonne du CSV qui lui correspond."
            : "Même principe pour le catalogue : le libellé, le prix, la TVA et le reste se choisissent colonne par colonne."}
        </p>
      </header>
      <div className="fx-co-card__foot" style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
        <Button disabled={busy} variant="ghost" onClick={() => void exp()}>
          Exporter
        </Button>
        <Button disabled={busy} onClick={() => input.current?.click()}>
          Choisir un CSV
        </Button>
        <input
          ref={input}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
          }}
        />
      </div>
      {staged ? (
        <div className="fx-ie-map">
          <p className="fx-co-form__hint">
            {staged.filename} — {staged.columns.length} colonne(s). Les correspondances reconnues sont déjà remplies.
          </p>
          <table>
            <thead>
              <tr>
                <th>Champ FacturX</th>
                <th>Colonne du fichier</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field) => (
                <tr key={field.id}>
                  <th scope="row">
                    {field.label}
                    {field.required ? <span className="fx-ie-map__req">obligatoire</span> : null}
                  </th>
                  <td>
                    <select
                      value={staged.map[field.id] || ""}
                      onChange={(e) =>
                        setStaged({ ...staged, map: { ...staged.map, [field.id]: e.target.value } })
                      }
                    >
                      <option value="">— Ne pas importer</option>
                      {staged.columns.map((col) => {
                        const sample = clip(staged.sample[col.key] || "");
                        return (
                          <option key={col.key} value={col.key}>
                            {col.label}
                            {sample ? ` — ${sample}` : ""}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {unused.length > 0 ? (
            <p className="fx-co-form__hint">
              Colonnes du fichier non utilisées : {unused.map((c) => c.label).join(", ")}.
            </p>
          ) : null}
          {missing.length > 0 ? (
            <p className="fx-co-form__err">Il manque : {missing.map((f) => f.label).join(", ")}.</p>
          ) : null}
          <div className="fx-ie-map__acts">
            <Button disabled={busy || missing.length > 0} onClick={() => void runImport()}>
              {busy ? "Import…" : "Importer"}
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setStaged(null);
                if (input.current) input.current.value = "";
              }}
            >
              Annuler
            </Button>
          </div>
        </div>
      ) : null}
      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
      {skipped.length > 0 ? (
        <ul className="fx-co-form__hint" style={{ marginTop: "0.6rem" }}>
          {skipped.map((s) => (
            <li key={`${s.line}-${s.message}`}>
              Ligne {s.line} — {s.message}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export function ImportExportTab() {
  const { company } = useCompany();
  if (!company) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <p className="fx-co-form__hint">
        Un client déjà là (même SIRET, sinon même nom) est mis à jour. Un produit déjà là (même code, sinon même libellé) aussi.
        Une fiche sans nom ou sans adresse complète est ignorée. Seules les colonnes que vous associez sont lues.
      </p>
      <Block entity="clients" />
      <Block entity="products" />
      <PriorTurnover />
    </div>
  );
}
